import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Controle segmentado (radiogroup): poucas opções mutuamente exclusivas, todas
 * visíveis. Setas movem e selecionam; só a opção marcada entra na ordem de Tab.
 *
 * Layout: colunas de largura IGUAL (`auto-cols-fr`), cada uma nunca menor que o texto mais largo. Antes eram itens
 * `flex-1 min-w-0` num contêiner de largura automática: o navegador calculava o controle por baixo, os botões ficavam
 * menores que o próprio rótulo e o texto vazava por cima do vizinho ("Colaboradores").
 */
export function Segmentado<T extends string>({
  rotulo,
  valor,
  aoMudar,
  opcoes,
  desabilitado = false,
  className,
  compacto = false,
}: {
  rotulo: string;
  valor: T;
  aoMudar: (valor: T) => void;
  opcoes: Array<{ valor: T; rotulo: string; icone?: React.ReactNode }>;
  desabilitado?: boolean;
  className?: string;
  compacto?: boolean;
}) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  function aoTeclar(evento: React.KeyboardEvent, indice: number) {
    const passo =
      evento.key === 'ArrowRight' || evento.key === 'ArrowDown'
        ? 1
        : evento.key === 'ArrowLeft' || evento.key === 'ArrowUp'
          ? -1
          : 0;
    if (!passo) return;
    evento.preventDefault();
    const proximo = (indice + passo + opcoes.length) % opcoes.length;
    refs.current[proximo]?.focus();
    aoMudar(opcoes[proximo].valor);
  }

  return (
    <div
      role="radiogroup"
      aria-label={rotulo}
      className={cn(
        'inline-grid max-w-full grid-flow-col auto-cols-fr gap-0.5 rounded-input border border-border-strong bg-bg-subtle p-0.5',
        className,
      )}
    >
      {opcoes.map((opcao, indice) => {
        const ativo = opcao.valor === valor;
        return (
          <button
            key={opcao.valor}
            ref={(no) => {
              refs.current[indice] = no;
            }}
            type="button"
            role="radio"
            aria-checked={ativo}
            tabIndex={ativo ? 0 : -1}
            disabled={desabilitado}
            onClick={() => !ativo && aoMudar(opcao.valor)}
            onKeyDown={(evento) => aoTeclar(evento, indice)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[5px] px-4 text-label transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:size-3.5 [&_svg]:shrink-0',
              compacto ? 'h-8' : 'h-9',
              ativo
                ? 'bg-bg-surface font-bold text-primary shadow-rest'
                : 'font-normal text-on-surface-muted hover:text-on-surface',
            )}
          >
            {opcao.icone}
            {opcao.rotulo}
          </button>
        );
      })}
    </div>
  );
}
